<script setup lang="ts">
import { CompanyService } from '@/services/CompanyService';
import { ReportingService } from '@/services/ReportingService';
import { Settings01Icon } from '@gohighlevel/ghl-icons/24/outline';
import { HLAlert, HLButton, HLDivider, HLHeaderLite, HLIcon, HLModal, HLText, HLToggle, useHLNotification } from '@platform-ui/highrise';
import { computed, h, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n()

const props = defineProps<{
    isVisible: boolean
    companyId: string
    isAutoScheduleEnabled: boolean
    isBulkActionInProgress: boolean
}>()
const emit = defineEmits(['update:isVisible', 'enable-bulk-action-in-progress', 'update:is-auto-schedule-enabled'])
const notification = useHLNotification()

const isModalVisible = computed({
    get: () => props.isVisible,
    set: (val) => emit('update:isVisible', val)
})
const isPrimaryBtnLoading = ref(false)
const shouldPauseReports = ref(false)
const isAutoScheduleToggleEnabled = ref(props.isAutoScheduleEnabled)
const isSaveDisabled = computed(() => {
    return isAutoScheduleToggleEnabled.value === props.isAutoScheduleEnabled
})
const isPauseAllSchedulesModalVisible = ref(false)

const handleSettingsModalConfirm = async () => {
    try {
        isPrimaryBtnLoading.value = true
        if (isAutoScheduleToggleEnabled.value !== props.isAutoScheduleEnabled) {
            await updateAutoSchedule()
        }

        isModalVisible.value = false
    } catch (e) {
    } finally {
        isPrimaryBtnLoading.value = false
    }
}

const bulkPauseReports = async () => {
    try {
        isPrimaryBtnLoading.value = true
        const body = {
            newStatus: 'draft',
            updateAll: true,
            currentStatus: 'published',
        }

        await ReportingService.bulkUpdateScheduleStatus(body, { companyId: props.companyId })

        const n = notification.create({
            content: () => h(HLAlert,
                {
                    title: t('success'),
                    closable: true,
                    id: 'pg_agency_location_reporting_schedule_settings_modal-notification-success_bulk_action_pause',
                    onClose: () => {
                        n.destroy()
                    },
                    type: 'notification',
                    color: 'green'
                },
                { default: t('scheduleBulkActionPauseSuccess') }
            ),
            duration: 5000,
        })
        emit('enable-bulk-action-in-progress')
        isModalVisible.value = false
    } catch (e) {
        console.error(e)
        const n = notification.create({
            content: () => h(HLAlert,
                {
                    title: t('error'),
                    closable: true,
                    id: 'pg_agency_location_reporting_schedule_settings_modal-notification-error_bulk_action_pause',
                    onClose: () => {
                        n.destroy()
                    },
                    type: 'notification',
                    color: 'red'
                },
                { default: t('somethingWrong') }
            ),
            duration: 5000,
        })
        throw e
    } finally {
        isPrimaryBtnLoading.value = false
    }
}

const updateAutoSchedule = async () => {
    try {
        await CompanyService.update(props.companyId, { rollupAutoScheduleEnabled: isAutoScheduleToggleEnabled.value })

        const n = notification.create({
            content: () => h(HLAlert,
                {
                    title: t('success'),
                    closable: true,
                    id: isAutoScheduleToggleEnabled.value
                        ? 'pg_agency_location_reporting_schedule_settings_modal-notification-success_auto_schedule_resume'
                        : 'pg_agency_location_reporting_schedule_settings_modal-notification-success_auto_schedule_pause',
                    onClose: () => {
                        n.destroy()
                    },
                    type: 'notification',
                    color: 'green'
                },
                { default: isAutoScheduleToggleEnabled.value ? t('autoScheduleEnabledForNewAccounts') : t('autoScheduleDisabledForNewAccounts') }
            ),
            duration: 5000,
        })
        emit('update:is-auto-schedule-enabled', isAutoScheduleToggleEnabled.value)
    } catch (e) {
        console.error(e)
        const n = notification.create({
            content: () => h(HLAlert,
                {
                    title: t('error'),
                    closable: true,
                    id: 'pg_agency_location_reporting_schedule_settings_modal-notification-error_auto_schedule_update',
                    onClose: () => {
                        n.destroy()
                    },
                    type: 'notification',
                    color: 'red'
                },
                { default: t('somethingWrong') }
            ),
            duration: 5000,
        })
        throw e
    }
}

watch(isModalVisible, (newVal) => {
    if (newVal) {
        shouldPauseReports.value = false
    }
})
</script>

<template>
    <HLModal id="pg_agency_location_reporting-modal-schedule_settings" v-model:show="isModalVisible" :width="577"
        :showHeaderIcon="false" :aria-label="t('scheduleSettingsModalTitle')">
        <template #header>
            <HLHeaderLite id="pg_agency_location_reporting_schedule_settings_modal-header" :closable="false" size="lg">
                <template #header-icons>
                    <div
                        class="rounded-xl h-10 w-10 border-[var(--gray-300)] border-1 border-solid box-border flex items-center justify-center mr-3">
                        <HLIcon size="20" aria-hidden="true">
                            <Settings01Icon />
                        </HLIcon>
                    </div>
                </template>
            </HLHeaderLite>
        </template>
        <HLText size="3xl" weight="semibold" class="text-gray-900">
            {{ $t('scheduleSettingsModalTitle') }}
        </HLText>
        <div class="mt-5 -mb-4">
            <HLText size="lg" weight="semibold" class="text-gray-900">
                {{ t('pauseSchedulesTitle') }}
            </HLText>
            <HLText size="lg" class="text-gray-600 mt-1">
                {{ t('pauseSchedulesDescription') }}
            </HLText>
            <HLButton variant="tertiary" color="blue" size="md" class="mt-4 mb-2"
                id="pg_agency_location_reporting_schedule_settings_modal-btn-pause_all_schedules"
                @click="isPauseAllSchedulesModalVisible = true" :disabled="isBulkActionInProgress">
                {{ t('pauseAllSchedulesBtnText') }}
            </HLButton>
            <HLDivider />
            <HLText size="lg" weight="semibold" class="text-gray-900 mt-2">
                {{ t('autoScheduleTitle') }}
            </HLText>
            <div class="flex gap-2 my-4">
                <HLToggle v-model:value="isAutoScheduleToggleEnabled"
                    id="pg_agency_location_reporting_schedule_settings_modal-toggle-auto_schedule" size="sm"
                    :aria-label="t('autoScheduleLabel')" :default-value="isAutoScheduleEnabled" />
                <HLText id="pg_agency_location_reporting_schedule_settings_modal-label-auto_schedule" size="md"
                    weight="medium" class="text-gray-700 font-medium">
                    {{ t('autoScheduleLabel') }}
                </HLText>
            </div>
        </div>
        <template #footer>
            <HLDivider />
            <div class="flex justify-end gap-3 m-6" id="pg_agency_location_reporting_schedule_settings_modal-footer">
                <HLButton id="pg_agency_location_reporting_schedule_settings_modal_footer-btn-cancel"
                    variant="secondary" color="gray" size="lg" @click="isModalVisible = false">
                    {{ t('cancel') }}
                </HLButton>
                <HLButton id="pg_agency_location_reporting_schedule_settings_modal_footer-btn-save_changes"
                    variant="primary" color="blue" size="lg" @click="handleSettingsModalConfirm"
                    :loading="isPrimaryBtnLoading" :disabled="isSaveDisabled">
                    {{ t('saveChanges') }}
                </HLButton>
            </div>
        </template>
    </HLModal>
    <HLModal id="pg_agency_location_reporting_schedule_settings-modal-pause_all_schedules_confirm" type="warning" v-model:show="isPauseAllSchedulesModalVisible"
        :mask-closable="false" :width="380">
        <template #header>
        </template>
        <HLText size="3xl" weight="semibold" class="text-gray-900">
            {{ $t('pauseAllSchedulesModalTitle') }}
        </HLText>
        <HLText size="lg" class="text-gray-600 mt-1">
            {{ $t('pauseAllSchedulesModalText') }}
        </HLText>
        <template #footer>
            <div class="flex justify-end gap-3 mx-6 mb-6 mt-2"
                id="pg_agency_location_reporting_schedule_settings_modal-footer-pause_all_schedules">
                <HLButton variant="secondary" color="gray" size="lg"
                    id="pg_agency_location_reporting_schedule_settings_modal-btn-pause_all_schedules_cancel"
                    @click="isPauseAllSchedulesModalVisible = false">
                    {{ $t('cancel') }}
                </HLButton>
                <HLButton variant="primary" color="blue" size="lg"
                    id="pg_agency_location_reporting_schedule_settings_modal-btn-pause_all_schedules_confirm"
                    @click="bulkPauseReports" :loading="isPrimaryBtnLoading">
                    {{ $t('confirm') }}
                </HLButton>
            </div>
        </template>
    </HLModal>
</template>